/**
 * Repositions side-attached fillers / vermin / panels when a parent (or the
 * accessory itself) changes width, depth, or exterior thickness.
 *
 * Side-button spawn / snap placement lives in `cabinetSideButtonHelpers`.
 * Filler flip / collision switching lives in `fillerSwitching`.
 */
import * as THREE from 'three';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {computeWorldBoundsExcludingAnimatedDoorParts} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    calculateSnappedPosition,
    isCabinetAdjacentOnSide,
} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import {
    handleFlipFillerProduct,
    HorizontalSide,
} from 'components/customer/RoomPlanner/helpers/fillerSwitching';

/** Filler / vermin / panel snapped to the side of a target cabinet. */
export type SideAttachment = {
    cabinet: CabinetMesh;
    side?: 'left' | 'right';
    axis: 'x' | 'z';
    boundary: 'min' | 'max';
    previousTargetBoundary: number;
    crossAxis: 'x' | 'z';
    previousTargetCrossMin: number;
    previousTargetCrossMax: number;
};

/** Parent cabinet a side-attached accessory is snapped to. */
export type SideAttachmentParent = {
    parent: CabinetMesh;
    attachment: SideAttachment;
};

export type RepositionAttachedSideProductsAfterDimensionChangeParams = {
    updatedCabinetGroup: CabinetMesh;
    attachedSideProducts: SideAttachment[];
    allCabinets: CabinetMesh[];
    fromBackup?: boolean;
    originalPositions?: Map<string, THREE.Vector3> | null;
    isLShapedCabinet: boolean;
    widthDifference: number;
    depthDifference: number;
    incrementalWidthDelta: number;
    incrementalDepthDelta: number;
    exteriorThicknessDifference: number;
    incrementalExteriorThicknessDelta: number;
    incrementalLeftDepthDelta: number;
    incrementalRightDepthDelta: number;
    hasLShapedAttachmentDimensionChange: boolean;
};

const SIDE_ATTACHMENT_TOUCH_TOLERANCE = 4;
const SIDE_ATTACHMENT_OVERLAP_TOLERANCE = 0.01;

const getSideAttachmentAxis = (targetCabinet: CabinetMesh): 'x' | 'z' => {
    const direction = targetCabinet.userData.currentDirection;

    return direction === 'left' || direction === 'right' ? 'z' : 'x';
};

const getBoxMinOnAxis = (box: THREE.Box3, axis: 'x' | 'y' | 'z'): number => {
    if (axis === 'x') return box.min.x;
    if (axis === 'y') return box.min.y;

    return box.min.z;
};

const getBoxMaxOnAxis = (box: THREE.Box3, axis: 'x' | 'y' | 'z'): number => {
    if (axis === 'x') return box.max.x;
    if (axis === 'y') return box.max.y;

    return box.max.z;
};

/**
 * Reads min or max of a box on the given horizontal axis.
 * @param {THREE.Box3} box - World-space bounds
 * @param {'x' | 'z'} axis - Horizontal axis
 * @param {'min' | 'max'} boundary - Which edge to read
 * @return {number} Boundary coordinate
 */
export const getBoxBoundaryOnAxis = (
    box: THREE.Box3,
    axis: 'x' | 'z',
    boundary: 'min' | 'max'
): number => {
    return boundary === 'min'
        ? getBoxMinOnAxis(box, axis)
        : getBoxMaxOnAxis(box, axis);
};

const getBoxCenterOnAxis = (box: THREE.Box3, axis: 'x' | 'z'): number => {
    return (getBoxMinOnAxis(box, axis) + getBoxMaxOnAxis(box, axis)) / 2;
};

const getRangeOverlap = (
    boxA: THREE.Box3,
    boxB: THREE.Box3,
    axis: 'x' | 'y' | 'z'
): number => {
    return (
        Math.min(getBoxMaxOnAxis(boxA, axis), getBoxMaxOnAxis(boxB, axis)) -
        Math.max(getBoxMinOnAxis(boxA, axis), getBoxMinOnAxis(boxB, axis))
    );
};

const getAttachmentTouchDistance = (
    attachmentBox: THREE.Box3,
    targetBox: THREE.Box3,
    axis: 'x' | 'z',
    boundary: 'min' | 'max'
): number => {
    if (boundary === 'min') {
        return Math.abs(
            getBoxMaxOnAxis(attachmentBox, axis) -
                getBoxMinOnAxis(targetBox, axis)
        );
    }

    return Math.abs(
        getBoxMinOnAxis(attachmentBox, axis) - getBoxMaxOnAxis(targetBox, axis)
    );
};

const hasDimensionDelta = (value: number): boolean =>
    value !== 0 && !isNaN(value);

/**
 * Gets the fillers and vermin panels that are touching the target cabinet on its left or right side
 * @param {CabinetMesh} targetCabinet - The target cabinet
 * @param {CabinetMesh[]} allCabinets - All the cabinets in the scene
 * @return {SideAttachment[]} The fillers and vermin panels that are touching the target cabinet on its left or right side
 */
export const getAttachedSideFillersAndVermins = (
    targetCabinet: CabinetMesh,
    allCabinets: CabinetMesh[]
): SideAttachment[] => {
    targetCabinet.updateMatrixWorld(true);
    const targetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(targetCabinet);
    const axis = getSideAttachmentAxis(targetCabinet);
    const crossAxis = axis === 'x' ? 'z' : 'x';
    const targetCenter = getBoxCenterOnAxis(targetBox, axis);
    const isLShaped = Boolean(targetCabinet.userData.isLShapedProduct);
    const cabinetDirection = targetCabinet.userData.currentDirection || 'front';

    const createAttachment = (
        cabinet: CabinetMesh,
        boundary: 'min' | 'max',
        side?: 'left' | 'right'
    ): SideAttachment => ({
        cabinet,
        side,
        axis,
        boundary,
        previousTargetBoundary: getBoxBoundaryOnAxis(targetBox, axis, boundary),
        crossAxis,
        previousTargetCrossMin: getBoxBoundaryOnAxis(
            targetBox,
            crossAxis,
            'min'
        ),
        previousTargetCrossMax: getBoxBoundaryOnAxis(
            targetBox,
            crossAxis,
            'max'
        ),
    });

    return allCabinets.reduce<SideAttachment[]>((attachments, cabinet) => {
        if (cabinet.uuid === targetCabinet.uuid) return attachments;

        if (
            !cabinet.userData.isFillerProduct &&
            !cabinet.userData.isVerminProduct &&
            !cabinet.userData.isPanelProduct
        ) {
            return attachments;
        }

        cabinet.updateMatrixWorld(true);
        const attachmentBox =
            computeWorldBoundsExcludingAnimatedDoorParts(cabinet);

        if (isLShaped) {
            const attachedSide = (['left', 'right'] as const).find((side) =>
                isCabinetAdjacentOnSide(
                    targetBox,
                    attachmentBox,
                    cabinetDirection,
                    true,
                    side
                )
            );

            if (attachedSide) {
                attachments.push(
                    createAttachment(
                        cabinet,
                        attachedSide === 'left' ? 'min' : 'max',
                        attachedSide
                    )
                );
            }

            return attachments;
        }

        const attachmentCenter = getBoxCenterOnAxis(attachmentBox, axis);
        const boundary = attachmentCenter < targetCenter ? 'min' : 'max';
        const touchDistance = getAttachmentTouchDistance(
            attachmentBox,
            targetBox,
            axis,
            boundary
        );
        const hasHeightOverlap =
            getRangeOverlap(attachmentBox, targetBox, 'y') >
            SIDE_ATTACHMENT_OVERLAP_TOLERANCE;
        const hasCrossAxisOverlap =
            getRangeOverlap(attachmentBox, targetBox, crossAxis) >
            SIDE_ATTACHMENT_OVERLAP_TOLERANCE;

        if (
            touchDistance <= SIDE_ATTACHMENT_TOUCH_TOLERANCE &&
            hasHeightOverlap &&
            hasCrossAxisOverlap
        ) {
            attachments.push(
                createAttachment(
                    cabinet,
                    boundary,
                    boundary === 'min' ? 'left' : 'right'
                )
            );
        }

        return attachments;
    }, []);
};

/**
 * Reverse lookup of `getAttachedSideFillersAndVermins`: finds the cabinet a
 * side-attached filler / vermin / panel is currently snapped to, so the
 * accessory can be re-aligned against that cabinet after it is re-rendered
 * (e.g. when its own material thickness changes).
 *
 * @param {CabinetMesh} accessory - Filler / vermin / panel product.
 * @param {CabinetMesh[]} allCabinets - All cabinets in the scene.
 * @return {SideAttachmentParent | null} Parent cabinet and the attachment descriptor, or null.
 */
export const findSideAttachmentParentForAccessory = (
    accessory: CabinetMesh,
    allCabinets: CabinetMesh[]
): SideAttachmentParent | null => {
    const isAccessory =
        accessory.userData.isFillerProduct ||
        accessory.userData.isVerminProduct ||
        accessory.userData.isPanelProduct;
    if (!isAccessory) return null;

    for (const candidate of allCabinets) {
        if (candidate.uuid === accessory.uuid) continue;
        if (
            candidate.userData.isFillerProduct ||
            candidate.userData.isVerminProduct ||
            candidate.userData.isPanelProduct
        ) {
            continue;
        }

        const attachment = getAttachedSideFillersAndVermins(candidate, [
            accessory,
        ]).find(({cabinet}) => cabinet.uuid === accessory.uuid);

        if (attachment) {
            return {parent: candidate, attachment};
        }
    }

    return null;
};

/**
 * Re-snaps side-attached fillers / vermin to a parent cabinet using the same
 * placement rules as the side-button add-on flow (`calculateSnappedPosition`).
 *
 * @param {CabinetMesh} targetCabinet - Re-rendered parent cabinet.
 * @param {SideAttachment[]} attachments - Side attachments from before re-render.
 * @param {CabinetMesh[]} allCabinets - All cabinets in the scene.
 * @param {object} [options] - Optional settings; set `updateCrossAxis` to false when repositioning an existing attachment.
 * @param {boolean} [options.crossAxisOnly] - When true with `updateCrossAxis`, only the depth axis is updated and the side-contact axis is preserved.
 * @return {void}
 */
export const repositionAttachedLShapedSideFillers = (
    targetCabinet: CabinetMesh,
    attachments: SideAttachment[],
    allCabinets: CabinetMesh[],
    options?: {
        updateCrossAxis?: boolean;
        useBoundaryDeltaOnly?: boolean;
        crossAxisOnly?: boolean;
    }
): void => {
    const updateCrossAxis = options?.updateCrossAxis ?? true;
    const useBoundaryDeltaOnly = options?.useBoundaryDeltaOnly ?? false;
    const crossAxisOnly = options?.crossAxisOnly ?? false;

    targetCabinet.updateMatrixWorld(true);
    const updatedTargetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(targetCabinet);

    attachments.forEach(
        ({
            cabinet,
            axis,
            boundary,
            side: attachedSide,
            previousTargetBoundary,
        }) => {
            if (useBoundaryDeltaOnly) {
                const updatedTargetBoundary = getBoxBoundaryOnAxis(
                    updatedTargetBox,
                    axis,
                    boundary
                );
                const boundaryDelta =
                    updatedTargetBoundary - previousTargetBoundary;

                if (boundaryDelta !== 0 && !isNaN(boundaryDelta)) {
                    if (axis === 'x') {
                        cabinet.position.x += boundaryDelta;
                    } else {
                        cabinet.position.z += boundaryDelta;
                    }
                }

                cabinet.updateMatrixWorld(true);
                return;
            }

            const side: 'left' | 'right' =
                attachedSide ?? (boundary === 'min' ? 'left' : 'right');
            const cabinetForPosition = cabinet.clone();
            const newPosition = calculateSnappedPosition(
                targetCabinet,
                cabinetForPosition,
                allCabinets,
                side
            );

            if (updateCrossAxis) {
                if (crossAxisOnly) {
                    const crossAxis = axis === 'x' ? 'z' : 'x';
                    if (crossAxis === 'z') {
                        cabinet.position.z = newPosition.z;
                    } else {
                        cabinet.position.x = newPosition.x;
                    }
                } else {
                    cabinet.position.copy(newPosition);
                }
            } else if (axis === 'x') {
                cabinet.position.x = newPosition.x;
            } else {
                cabinet.position.z = newPosition.z;
            }

            cabinet.updateMatrixWorld(true);
        }
    );
};

/**
 * Re-aligns a re-rendered side-attached filler / vermin against its parent
 * cabinet after the accessory's own dimensions (e.g. material thickness)
 * changed. The front axis is recomputed with the same snap rules used when
 * the accessory was added, so it always lands on the parent's exterior face —
 * including when the parent's carcase / exterior thickness was customised.
 * The side-contact axis is then closed up via bounding boxes so the accessory
 * keeps touching the parent even when the new geometry grew or shrunk around
 * the group origin (flipped fillers grow on the opposite side of the origin).
 *
 * @param {CabinetMesh} parent - Cabinet the accessory is attached to.
 * @param {SideAttachment} attachment - Attachment descriptor captured before the accessory re-render.
 * @param {CabinetMesh} updatedAccessory - Newly rendered accessory group.
 * @param {CabinetMesh[]} allCabinets - All cabinets in the scene.
 * @return {void}
 */
export const realignSideAttachmentToParent = (
    parent: CabinetMesh,
    attachment: SideAttachment,
    updatedAccessory: CabinetMesh,
    allCabinets: CabinetMesh[]
): void => {
    repositionAttachedLShapedSideFillers(
        parent,
        [{...attachment, cabinet: updatedAccessory}],
        allCabinets,
        {
            updateCrossAxis: true,
            crossAxisOnly: true,
        }
    );

    // L-shaped parents attach along wing faces, not the AABB boundary, so the
    // side-contact correction below only applies to normal cabinets.
    if (parent.userData.isLShapedProduct) return;

    parent.updateMatrixWorld(true);
    updatedAccessory.updateMatrixWorld(true);
    const parentBox = computeWorldBoundsExcludingAnimatedDoorParts(parent);
    const accessoryBox =
        computeWorldBoundsExcludingAnimatedDoorParts(updatedAccessory);
    const {axis, boundary} = attachment;
    const parentBoundary = getBoxBoundaryOnAxis(parentBox, axis, boundary);
    const accessoryTouchBoundary = getBoxBoundaryOnAxis(
        accessoryBox,
        axis,
        boundary === 'min' ? 'max' : 'min'
    );
    const sideDelta = parentBoundary - accessoryTouchBoundary;

    if (sideDelta !== 0 && !isNaN(sideDelta)) {
        if (axis === 'x') {
            updatedAccessory.position.x += sideDelta;
        } else {
            updatedAccessory.position.z += sideDelta;
        }
        updatedAccessory.updateMatrixWorld(true);
    }
};

/**
 * Repositions side-attached fillers / vermin / panels after a parent cabinet
 * width, depth, or exterior-thickness change.
 * @param {RepositionAttachedSideProductsAfterDimensionChangeParams} params - Dimension deltas and attachment list
 * @return {boolean} True when positions were restored from backup
 */
export const repositionAttachedSideProductsAfterDimensionChange = ({
    updatedCabinetGroup,
    attachedSideProducts,
    allCabinets,
    fromBackup,
    originalPositions,
    isLShapedCabinet,
    widthDifference,
    depthDifference,
    incrementalWidthDelta,
    incrementalDepthDelta,
    exteriorThicknessDifference,
    incrementalExteriorThicknessDelta,
    incrementalLeftDepthDelta,
    incrementalRightDepthDelta,
    hasLShapedAttachmentDimensionChange,
}: RepositionAttachedSideProductsAfterDimensionChangeParams): boolean => {
    const shouldRepositionAttachedSideProducts =
        attachedSideProducts.length > 0 &&
        (fromBackup ||
            hasDimensionDelta(widthDifference) ||
            hasDimensionDelta(depthDifference) ||
            hasDimensionDelta(incrementalWidthDelta) ||
            hasDimensionDelta(incrementalDepthDelta) ||
            hasDimensionDelta(exteriorThicknessDifference) ||
            hasDimensionDelta(incrementalExteriorThicknessDelta) ||
            hasLShapedAttachmentDimensionChange);

    if (!shouldRepositionAttachedSideProducts) {
        return false;
    }

    const hasExteriorThicknessChange =
        hasDimensionDelta(exteriorThicknessDifference) ||
        hasDimensionDelta(incrementalExteriorThicknessDelta);
    const hasNormalWidthChange =
        !isLShapedCabinet &&
        (hasDimensionDelta(widthDifference) ||
            hasDimensionDelta(incrementalWidthDelta));
    const hasNormalDepthChange =
        !isLShapedCabinet &&
        (hasDimensionDelta(depthDifference) ||
            hasDimensionDelta(incrementalDepthDelta));
    // Depth / exterior thickness move the front (cross) axis. Re-snap that axis
    // only — a full snap also rewrites the side-contact axis and overlaps
    // right-side fillers (they are flipped; vermin are not).
    // Front Z must use calculateSnappedPosition (doorThickness offset), not a
    // parent carcase nudge: thickness moves the cabinet back while doors grow
    // forward, so copying the parent nudge pulls the filler off the front face.
    const isNormalCabinetCrossAxisOnlyChange =
        !isLShapedCabinet &&
        !hasNormalWidthChange &&
        (hasNormalDepthChange || hasExteriorThicknessChange);
    const isNormalCabinetWidthWithCrossAxisChange =
        hasNormalWidthChange &&
        (hasNormalDepthChange || hasExteriorThicknessChange);
    const hasLShapedRightWidthChange =
        isLShapedCabinet &&
        hasDimensionDelta(incrementalDepthDelta) &&
        incrementalWidthDelta === 0 &&
        incrementalLeftDepthDelta === 0 &&
        incrementalRightDepthDelta === 0 &&
        !hasExteriorThicknessChange;
    const hasLShapedLeftWidthChange =
        isLShapedCabinet &&
        hasDimensionDelta(incrementalWidthDelta) &&
        incrementalDepthDelta === 0 &&
        incrementalLeftDepthDelta === 0 &&
        incrementalRightDepthDelta === 0 &&
        !hasExteriorThicknessChange;

    updatedCabinetGroup.updateMatrixWorld(true);

    if (fromBackup && originalPositions?.size) {
        attachedSideProducts.forEach(({cabinet: attachedCabinet}) => {
            const savedPosition = originalPositions.get(attachedCabinet.uuid);
            if (savedPosition) {
                attachedCabinet.position.copy(savedPosition);
                attachedCabinet.updateMatrixWorld(true);
            }
        });

        return true;
    }

    const shouldUpdateAttachedCrossAxis =
        fromBackup || hasNormalDepthChange || hasExteriorThicknessChange;

    if (isLShapedCabinet && hasLShapedAttachmentDimensionChange) {
        const lShapedAttachmentsForReposition = hasLShapedRightWidthChange
            ? attachedSideProducts.filter(({side}) => side === 'right')
            : hasLShapedLeftWidthChange
              ? attachedSideProducts.filter(({side}) => side === 'right')
              : attachedSideProducts;
        const isLShapedWidthOnlyChange =
            hasLShapedRightWidthChange || hasLShapedLeftWidthChange;

        repositionAttachedLShapedSideFillers(
            updatedCabinetGroup,
            lShapedAttachmentsForReposition,
            allCabinets,
            isLShapedWidthOnlyChange
                ? {
                      useBoundaryDeltaOnly: true,
                  }
                : undefined
        );
    } else if (isNormalCabinetCrossAxisOnlyChange) {
        repositionAttachedLShapedSideFillers(
            updatedCabinetGroup,
            attachedSideProducts,
            allCabinets,
            {
                updateCrossAxis: true,
                crossAxisOnly: true,
            }
        );
    } else if (isNormalCabinetWidthWithCrossAxisChange) {
        // Width moves the side-contact axis; depth/thickness moves the front axis.
        repositionAttachedLShapedSideFillers(
            updatedCabinetGroup,
            attachedSideProducts,
            allCabinets,
            {
                useBoundaryDeltaOnly: true,
            }
        );
        repositionAttachedLShapedSideFillers(
            updatedCabinetGroup,
            attachedSideProducts,
            allCabinets,
            {
                updateCrossAxis: true,
                crossAxisOnly: true,
            }
        );
    } else {
        repositionAttachedLShapedSideFillers(
            updatedCabinetGroup,
            attachedSideProducts,
            allCabinets,
            {
                updateCrossAxis: shouldUpdateAttachedCrossAxis,
                useBoundaryDeltaOnly: !shouldUpdateAttachedCrossAxis,
            }
        );
    }

    return false;
};

/**
 * Nudge a cabinet group when exterior material thickness changes so it stays
 * flush with the wall / parent face (fillers, vermin, and side-attached products).
 *
 * @param {THREE.Object3D} group - Cabinet group to move.
 * @param {string} facing - `userData.currentDirection` of the edited parent cabinet.
 * @param {number} exteriorThicknessDifference - Thickness delta in mm (unscaled).
 * @param {number} scale - Room scale factor.
 * @param {boolean} attachSide - Whether the product is side-attached.
 * @return {void}
 */
export function applyExteriorThicknessPositionNudge(
    group: THREE.Object3D,
    facing: string,
    exteriorThicknessDifference: number,
    scale: number,
    attachSide: boolean
): void {
    if (
        exteriorThicknessDifference === 0 ||
        isNaN(exteriorThicknessDifference)
    ) {
        return;
    }

    const delta = exteriorThicknessDifference * scale;
    if (attachSide) {
        if (facing === 'front' || facing === 'normal') {
            group.position.z += delta;
        } else if (facing === 'back') {
            group.position.z -= delta;
        } else if (facing === 'left') {
            group.position.x += delta;
        } else if (facing === 'right') {
            group.position.x -= delta;
        }
    } else {
        if (facing === 'front' || facing === 'normal') {
            group.position.z -= delta;
        } else if (facing === 'back') {
            group.position.z += delta;
        } else if (facing === 'left') {
            group.position.x -= delta;
        } else if (facing === 'right') {
            group.position.x += delta;
        }
    }
}

/**
 * When a normal parent cabinet's depth changes, nudge side-attached fillers /
 * vermin on the depth axis only — same direction as the parent depth fix in
 * `useRoomPlanner`. Preserves the side-contact axis so re-snap does not shift
 * the accessory into the cabinet.
 *
 * @param {THREE.Object3D} group - Side-attached filler / vermin group.
 * @param {string} parentFacing - Parent `userData.currentDirection`.
 * @param {number} depthDeltaMm - Depth change in mm (unscaled).
 * @param {number} scale - Room scale factor.
 * @return {void}
 */
export function applyAttachedSideProductDepthNudge(
    group: THREE.Object3D,
    parentFacing: string,
    depthDeltaMm: number,
    scale: number
): void {
    if (depthDeltaMm === 0 || isNaN(depthDeltaMm)) {
        return;
    }

    const delta = depthDeltaMm * scale;

    // Match the parent depth nudge in useRoomPlanner (same sign per facing).
    if (parentFacing === 'front' || parentFacing === 'normal') {
        group.position.z += delta;
    } else if (parentFacing === 'back') {
        group.position.z -= delta;
    } else if (parentFacing === 'left') {
        group.position.x += delta;
    } else if (parentFacing === 'right') {
        group.position.x -= delta;
    }
}

/**
 * When an L-shaped parent cabinet's right width changes, side-attached fillers on
 * the outer (max) edge may not see a bbox boundary shift. Nudge them incrementally.
 *
 * @param {THREE.Object3D} group - Attached filler / vermin group.
 * @param {'x' | 'z'} axis - Side-attachment axis from `getAttachedSideFillersAndVermins`.
 * @param {'min' | 'max'} boundary - Which target face the product is snapped to.
 * @param {number} incrementalWidthDelta - Width change since last value (mm, unscaled).
 * @param {number} scale - Room scale factor.
 * @return {void}
 */
export function applyAttachedLShapedWidthPositionNudge(
    group: THREE.Object3D,
    axis: 'x' | 'z',
    boundary: 'min' | 'max',
    incrementalWidthDelta: number,
    scale: number
): void {
    if (
        incrementalWidthDelta === 0 ||
        isNaN(incrementalWidthDelta) ||
        boundary !== 'max'
    ) {
        return;
    }

    const delta = incrementalWidthDelta * scale;

    if (axis === 'x') {
        group.position.x += delta;
    } else {
        group.position.z += delta;
    }
}

/**
 * After a width edit, re-rendered filler has lost negative scale; nudge position and re-apply flip.
 * Branches on `userData.fillerFlipSource` (`'wall'` vs cabinet/`'none'`) for wall-specific behavior.
 *
 * @param {THREE.Object3D} group - Re-rendered cabinet group.
 * @param {string} facing - `userData.currentDirection`.
 * @param {number} scaledWidthDelta - Width change × room scale.
 * @return {void}
 */
export function applyFlippedFillerWidthResizeNudge(
    group: THREE.Object3D,
    facing: string,
    scaledWidthDelta: number
): void {
    const applyFacingDelta = (facing: string, delta: number) => {
        if (facing === 'front' || facing === 'normal') {
            if (group.userData.isVerminProduct) {
                group.position.x -= delta;
            } else {
                group.position.x += delta;
            }
        } else if (facing === 'back') {
            if (group.userData.isVerminProduct) {
                group.position.x += delta;
            } else {
                group.position.x -= delta;
            }
        } else if (facing === 'left') {
            if (group.userData.isVerminProduct) {
                group.position.z += delta;
            } else {
                group.position.z -= delta;
            }
        } else if (facing === 'right') {
            if (group.userData.isVerminProduct) {
                group.position.z -= delta;
            } else {
                group.position.z += delta;
            }
        }
    };

    applyFacingDelta(facing, scaledWidthDelta);
    group.updateMatrixWorld();
    handleFlipFillerProduct(group, HorizontalSide.Right);
}
